import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Send, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = "https://vantatweaks.onrender.com/api";
const DISCORD = 'https://discord.gg/yzM2p4sFb4';

const SUGGESTIONS = [
  'Is V2 safe for my PC?',
  'What\'s the difference between V1 and V2?',
  'How does the purchase work?',
];

export default function AISupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'ai', text: 'Hi! I\'m Vanta AI. Ask me anything about our products or performance gains.' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2));
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = text.trim();
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/support/chat`, { session_id: sessionId, message: userMsg });
      setMessages(prev => [...prev, { role: 'ai', text: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: 'Something went wrong. Please try again or reach us on Discord.' }]);
    } finally {
      setLoading(false);
    }
  }, [loading, sessionId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        data-testid="ai-support-btn"
        onClick={() => setOpen(!open)}
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2, type: 'spring', stiffness: 200 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-[100] flex items-center gap-2 px-4 py-3 rounded-full border border-white/20 backdrop-blur-2xl font-inter text-sm font-medium text-white cursor-pointer"
        style={{
          background: 'rgba(255,255,255,0.08)',
          boxShadow: open ? '0 0 30px rgba(255,255,255,0.15)' : '0 0 20px rgba(255,255,255,0.06)',
        }}
      >
        <motion.div
          animate={!open ? { boxShadow: ['0 0 0px rgba(255,255,255,0.3)', '0 0 12px rgba(255,255,255,0.5)', '0 0 0px rgba(255,255,255,0.3)'] } : {}}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {open ? <X size={16} /> : <Sparkles size={16} />}
        </motion.div>
        {!open && 'AI Support'}
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-20 right-6 z-[100] w-80 rounded-3xl border border-white/15 overflow-hidden"
            style={{
              background: 'rgba(5,5,8,0.92)',
              backdropFilter: 'blur(30px)',
              boxShadow: '0 0 80px rgba(255,255,255,0.06), 0 25px 50px rgba(0,0,0,0.5)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-full border border-white/20 bg-white/8 flex items-center justify-center">
                  <Sparkles size={13} className="text-white" />
                </div>
                <div>
                  <p className="font-outfit text-sm font-medium text-white leading-tight">Vanta AI</p>
                  <p className="font-inter text-[10px] text-green-400 flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-green-400 animate-pulse" />
                    Online
                  </p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-zinc-600 hover:text-white transition-colors">
                <X size={15} />
              </button>
            </div>

            {/* Messages */}
            <div className="h-72 overflow-y-auto p-4 space-y-3 scrollbar-none" style={{ scrollbarWidth: 'none' }}>
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl font-inter text-xs leading-relaxed
                      ${msg.role === 'user'
                        ? 'bg-white text-black rounded-br-sm'
                        : 'bg-white/8 text-zinc-300 rounded-bl-sm border border-white/8'
                      }`}
                  >
                    {msg.text}
                  </div>
                </motion.div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-white/8 border border-white/8 flex gap-1.5">
                    {[0, 1, 2].map(i => (
                      <div key={i} className="typing-dot w-1.5 h-1.5 rounded-full bg-zinc-400" />
                    ))}
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Suggestions */}
            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-col gap-1.5">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => sendMessage(s)}
                    className="text-left px-3 py-2 rounded-xl border border-white/8 bg-white/[0.03] text-zinc-500 font-inter text-xs hover:border-white/20 hover:text-white transition-all duration-200"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <form onSubmit={handleSubmit} className="flex items-center gap-2 px-4 py-3 border-t border-white/8">
              <input
                data-testid="ai-chat-input"
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Ask anything..."
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs font-inter text-white placeholder:text-zinc-700 focus:outline-none focus:border-white/25 transition-colors"
              />
              <button type="submit" disabled={!input.trim() || loading}
                className="w-8 h-8 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center text-white hover:bg-white/20 transition-all disabled:opacity-40"
              >
                <Send size={12} />
              </button>
            </form>

            {/* Discord fallback */}
            <div className="px-4 pb-4">
              <button onClick={() => window.open(DISCORD, '_blank', 'noopener,noreferrer')}
                className="w-full flex items-center justify-center gap-1.5 py-2 font-inter text-[10px] text-zinc-700 hover:text-zinc-500 transition-colors"
              >
                <ExternalLink size={9} /> Talk to a human on Discord
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
